import {
  decodeAppearancePayload,
  encodeAppearancePayload,
  type Appearance,
} from '@/shared/appearance/appearance'
import { isLocale, type Locale } from '@/shared/i18n/locale'

/**
 * The one browser-owned hint used before an account is known.
 *
 * This is deliberately separate from the HttpOnly session cookie and from the
 * authenticated account preference stores. It contains only closed product
 * vocabulary, never an account id, username, credential, token or session
 * state. The version lives in the key so an incompatible future shape can
 * start cleanly without guessing how to reinterpret old bytes.
 */
const LOGIN_PREFERENCES_KEY = 'terysling.login-preferences.v1'

export interface LoginPreferences {
  locale?: Locale
  appearance?: Appearance
}

function recordOf(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

/** Treat browser-owned bytes as untrusted input before they reach the document. */
export function decodeLoginPreferences(value: unknown): LoginPreferences {
  const payload = recordOf(value)
  if (!payload) {
    return {}
  }

  const locale = payload['locale']
  const appearancePayload = recordOf(payload['appearance'])
  const appearance = appearancePayload ? decodeAppearancePayload(appearancePayload) : null

  return {
    ...(isLocale(locale) ? { locale } : {}),
    ...(appearance ? { appearance } : {}),
  }
}

export function readLoginPreferences(): LoginPreferences {
  if (typeof window === 'undefined') {
    return {}
  }
  try {
    const stored = window.localStorage.getItem(LOGIN_PREFERENCES_KEY)
    return stored === null ? {} : decodeLoginPreferences(JSON.parse(stored) as unknown)
  } catch {
    // Storage may be disabled and its contents may be malformed. Either case
    // returns the ordinary product defaults instead of blocking sign-in.
    return {}
  }
}

function writeLoginPreferences(next: LoginPreferences): void {
  if (typeof window === 'undefined') {
    return
  }
  try {
    window.localStorage.setItem(
      LOGIN_PREFERENCES_KEY,
      JSON.stringify({
        ...(next.locale ? { locale: next.locale } : {}),
        ...(next.appearance
          ? { appearance: encodeAppearancePayload(next.appearance) }
          : {}),
      }),
    )
  } catch {
    // Remembering is an enhancement. A storage refusal must not make either
    // picker or the sign-in form fail.
  }
}

export function rememberLoginLocale(locale: Locale): void {
  writeLoginPreferences({ ...readLoginPreferences(), locale })
}

export function rememberLoginAppearance(appearance: Appearance): void {
  writeLoginPreferences({ ...readLoginPreferences(), appearance })
}

/** Used by test isolation today and available to a future browser-data reset. */
export function clearLoginPreferences(): void {
  if (typeof window === 'undefined') {
    return
  }
  try {
    window.localStorage.removeItem(LOGIN_PREFERENCES_KEY)
  } catch {
    // Clearing an unavailable store is already the desired outcome.
  }
}
